import { useState } from 'react';
import type { SyncedOrderType } from '@/types/api';

interface OrderTypeFormProps {
  formData: Partial<SyncedOrderType>;
  onChange: (data: Partial<SyncedOrderType>) => void;
}

export function OrderTypeForm({ formData, onChange }: OrderTypeFormProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    basic: true,
    orderFlow: true,
    display: false,
    kitchen: false,
    printing: false,
    booking: false,
    tipFooter: false,
    other: false,
  });

  const toggle = (section: string) =>
    setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));

  const checkbox = (label: string, field: keyof SyncedOrderType, defaultVal = false) => (
    <label className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={(formData[field] as boolean) ?? defaultVal}
        onChange={(e) => onChange({ ...formData, [field]: e.target.checked })}
        className="rounded border-gray-300"
      />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );

  const numberInput = (label: string, field: keyof SyncedOrderType, defaultVal = 0) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="number"
        value={(formData[field] as number) ?? defaultVal}
        onChange={(e) => onChange({ ...formData, [field]: e.target.value ? parseInt(e.target.value) : undefined })}
        className="input w-full"
      />
    </div>
  );

  const textInput = (label: string, field: keyof SyncedOrderType, required = false) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required ? ' *' : ''}</label>
      <input
        type="text"
        value={(formData[field] as string) || ''}
        onChange={(e) => onChange({ ...formData, [field]: e.target.value || undefined })}
        className="input w-full"
        required={required}
      />
    </div>
  );

  const section = (key: string, title: string, content: React.ReactNode) => (
    <div className="border border-gray-200 rounded-lg">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-t-lg"
        onClick={() => toggle(key)}
      >
        {title}
        <span className="text-gray-400">{expanded[key] ? '−' : '+'}</span>
      </button>
      {expanded[key] && <div className="p-4 space-y-4">{content}</div>}
    </div>
  );

  return (
    <div className="space-y-4">
      {section('basic', 'Basic', <>
        {textInput('Name', 'name', true)}
        {numberInput('Sort Order', 'sortOrder')}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={formData.colorCode != null ? '#' + (formData.colorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : '#ffffff'}
                onChange={(e) => onChange({ ...formData, colorCode: parseInt(e.target.value.slice(1), 16) })}
                className="h-9 w-14 cursor-pointer rounded border border-gray-300 p-0.5"
              />
              <input
                type="text"
                value={formData.colorCode != null ? '#' + (formData.colorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : ''}
                onChange={(e) => {
                  const hex = e.target.value.replace('#', '');
                  if (/^[0-9a-fA-F]{0,6}$/.test(hex)) {
                    onChange({ ...formData, colorCode: hex.length === 6 ? parseInt(hex, 16) : undefined });
                  }
                }}
                placeholder="#ffffff"
                className="input w-full"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={formData.textColor != null ? '#' + (formData.textColor >>> 0).toString(16).padStart(6, '0').slice(-6) : '#000000'}
                onChange={(e) => onChange({ ...formData, textColor: parseInt(e.target.value.slice(1), 16) })}
                className="h-9 w-14 cursor-pointer rounded border border-gray-300 p-0.5"
              />
              <input
                type="text"
                value={formData.textColor != null ? '#' + (formData.textColor >>> 0).toString(16).padStart(6, '0').slice(-6) : ''}
                onChange={(e) => {
                  const hex = e.target.value.replace('#', '');
                  if (/^[0-9a-fA-F]{0,6}$/.test(hex)) {
                    onChange({ ...formData, textColor: hex.length === 6 ? parseInt(hex, 16) : undefined });
                  }
                }}
                placeholder="#000000"
                className="input w-full"
              />
            </div>
          </div>
        </div>
        {formData.colorCode != null && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
            <div
              className="rounded px-4 py-2 text-center font-medium"
              style={{
                backgroundColor: '#' + ((formData.colorCode ?? 0xffffff) >>> 0).toString(16).padStart(6, '0').slice(-6),
                color: '#' + ((formData.textColor ?? 0) >>> 0).toString(16).padStart(6, '0').slice(-6),
              }}
            >
              {formData.name || 'Order Type'}
            </div>
          </div>
        )}
      </>)}

      {section('orderFlow', 'Order Flow', <>
        <div className="grid grid-cols-2 gap-4">
          {checkbox('Enabled', 'isEnabled', true)}
          {checkbox('Prepaid', 'isPrepaid')}
          {checkbox('Delivery', 'isDelivery')}
          {checkbox('Kiosk', 'isKiosk')}
          {checkbox('Bar Tab', 'isBarTab')}
          {checkbox('Close on Paid', 'isCloseOnPaid')}
          {checkbox('Required Customer Data', 'isRequiredCustomerData')}
          {checkbox('Has For Here and To Go', 'isHasForHereAndToGo')}
          {checkbox('Staff Meal', 'isStaffMeal')}
          {checkbox('Enforce Delivery Charge', 'isEnforceDeliveryCharge')}
          {checkbox('Wasted', 'isWasted')}
          {checkbox('External Delivery Provider', 'isExternalDeliveryProvider')}
          {checkbox('Has Z Report', 'isHasZReport')}
          {checkbox('Pre-Auth Credit Card', 'isPreAuthCreditCard')}
        </div>
      </>)}

      {section('display', 'Display', <>
        <div className="grid grid-cols-2 gap-4">
          {checkbox('Show Table Selection', 'isShowTableSelection')}
          {checkbox('Show Guest Selection', 'isShowGuestSelection')}
          {checkbox('Show In Login Screen', 'isShowInLoginScreen')}
          {checkbox('Show Price On Button', 'isShowPriceOnButton')}
          {checkbox('Show Stock Count On Button', 'isShowStockCountOnButton')}
          {checkbox('Show Unit Price In Ticket Grid', 'isShowUnitPriceInTicketGrid')}
          {checkbox('Show Item Barcode', 'isShowItemBarcode')}
          {checkbox('Allow Seat Based Order', 'isAllowSeatBasedOrder')}
          {checkbox('Hide Item With Empty Inventory', 'isHideItemWithEmptyInventory')}
          {checkbox('Show In Dispatch Order', 'isShowInDispatchOrder')}
        </div>
      </>)}

      {section('kitchen', 'Kitchen', <>
        <div className="grid grid-cols-2 gap-4">
          {checkbox('Should Print To Kitchen', 'isShouldPrintToKitchen')}
          {checkbox('Send To Bar', 'isSendToBar')}
          {checkbox('No Print To Bar', 'isNoPrintToBar')}
          {checkbox('Auto Send To Kitchen When Needed', 'isAutomaticallySendToKitchen')}
          {checkbox('Manually Send To Kitchen', 'isManuallySendToKitchen')}
          {checkbox('Use Kitchen Display Order', 'isUseKitchenDisplayOrder')}
          {checkbox('Close Kitchen Ticket When Close Ticket', 'isCloseKitchenTicketWhenCloseTicket')}
          {checkbox('Allow Pay Tickets With Service Charge', 'isAllowPayTicketsWithServiceCharge')}
        </div>
        {numberInput('Time In Advance To Send To Kitchen', 'timeInAdvanceToSendToKitchen')}
      </>)}

      {section('printing', 'Printing', <>
        <div className="grid grid-cols-2 gap-4">
          {checkbox('Print Ticket Number', 'isPrintTicketNumber')}
          {checkbox('Print Receipt On Payment Done', 'isPrintReceiptOnPaymentDone')}
          {checkbox('Print Short Receipt', 'isPrintShortReceipt', true)}
          {checkbox('Print On Booking Complete', 'isPrintOnBookingComplete', true)}
          {checkbox('Print Kitchen On Payment Complete', 'isPrintKitchenOnPaymentComplete', true)}
          {checkbox('Print Ticket Instead Kitchen Ticket', 'isPrintTicketInstedKitchenTicket')}
          {checkbox('Consolidate Items In Receipt', 'isConsolidateItemsInReceipt')}
          {checkbox('Show Cooking Instruction On Receipt', 'isShowCookingInstructionOnReceipt')}
          {checkbox('Show KDO On Ticket Receipt', 'isShowKitchenDisplayOrderOnTicketReceipt')}
          {checkbox('Print Payment Info', 'isPrintPaymentInfo')}
          {checkbox('Show Unpaid On Receipt', 'isShowUnpaidOnReceipt')}
          {checkbox('Show Value On Payment Info', 'isShowValueOnPaymentInfo')}
          {checkbox('Not Print Online On Settle', 'isNotPrintOnlineOnSettle')}
          {checkbox('Ask Payment On Done', 'isAskPaymentOnDone')}
          {checkbox('Sort Items By Group In Receipt', 'isSortItemsByGroupInReceipt')}
        </div>
        {numberInput('Time To Print On Payment Done', 'timeToPrintOnPaymentDone')}
        <div className="grid grid-cols-2 gap-4">
          {textInput('Ticket Receipt Name', 'ticketReceiptName')}
          {textInput('Kitchen Receipt Name', 'kitchenReceiptName')}
        </div>
        {textInput('Bar Receipt Name', 'barReceiptName')}
      </>)}

      {section('booking', 'Booking', <>
        {textInput('Booking Type', 'bookingType')}
        <div className="grid grid-cols-2 gap-4">
          {numberInput('Booking Request Timeout', 'bookingRequestTimeout', 5)}
          {numberInput('Booking Rejected Time Default', 'bookingRejectedTimeDefault', 20)}
        </div>
        {numberInput('Temporary Approve Time', 'temporaryApproveTime', 50)}
        {textInput('Auto Approved Statuses', 'autoApprovedStatuses')}
        {textInput('Booking Messages', 'bookingMessages')}
        <div className="grid grid-cols-2 gap-4">
          {checkbox('Approve Without Confirmation', 'isApproveWithoutConfirmation')}
          {checkbox('Preapproved', 'isPreapproved', true)}
          {checkbox('Auto-approve Simple Bookings', 'isAutoaproveSimpleBookings')}
          {checkbox('Auto-approve Booking Changes', 'isAutoaproveBookingChanges')}
          {checkbox('Play Auto-approve Sound', 'isPlayAutoaproveSound')}
        </div>
      </>)}

      {section('tipFooter', 'Tip & Footer', <>
        <div className="grid grid-cols-2 gap-4">
          {checkbox('Show Tip Suggestion', 'isShowTipSugestion')}
        </div>
        {textInput('Tip Suggestion Percentages', 'tipSugestionPercentages')}
        {textInput('Footer Message', 'footerMessage')}
      </>)}

      {section('other', 'Other', <>
        <div className="grid grid-cols-2 gap-4">
          {numberInput('Start Daily ID From', 'startDailyIdFrom')}
          {numberInput('Minute ASAP', 'minuteAsap', 10)}
        </div>
        {textInput('Label Time Requested', 'labelTimeRequested')}
        {textInput('Customer QR Code', 'customerQRCode')}
        {textInput('Add Products', 'addProducts')}
      </>)}
    </div>
  );
}